/**
 * Type Properties Service — the BIM "Edit Type" write path.
 *
 * Responsibilities:
 * - Parse the `"Family: Type"` string reference that elements carry.
 * - Guarantee a registry symbol exists for any element type string (freeform
 *   names written by older tools are promoted on first edit).
 * - Propagate type-level parameter edits to ALL instances of the type in one
 *   undoable kernel command, mirroring geometry-affecting values into
 *   geometryData the same way the placement tools derive them.
 */

import { familyManager } from './FamilyManager';
import { bimKernel } from './BIMKernel';
import { BIMElement, CompoundStructureLayer } from './types';

export interface TypeRef {
    family: string;
    typeName: string;
}

/** Split the canonical `"Family: Type"` element type string. */
export function parseTypeString(type: string | undefined): TypeRef | null {
    if (!type) return null;
    const idx = type.indexOf(': ');
    if (idx <= 0) return null;
    const family = type.slice(0, idx).trim();
    const typeName = type.slice(idx + 2).trim();
    if (!family || !typeName) return null;
    return { family, typeName };
}

export function formatTypeString(ref: TypeRef): string {
    return `${ref.family}: ${ref.typeName}`;
}

/**
 * Ensure a symbol exists for the element's type string, creating a family
 * entry (under the element category) and seeding the type's parameters from
 * the element's current type-classified values when needed. This makes Edit
 * Type work for every element, including freeform tool-written type names.
 */
export function ensureSymbolForElement(element: BIMElement): TypeRef {
    const ref = parseTypeString(element.type) ?? { family: element.type || 'Unnamed Family', typeName: element.name || 'Default' };
    const category = element.category || 'Generic Models';

    const existing = familyManager.getType(ref.family, ref.typeName);
    if (existing) return ref;

    const family = familyManager.ensureFamily(ref.family, category);

    // Seed the new symbol with the type-level values found on the element so
    // the dialog opens with sensible defaults instead of an empty row set.
    const seed: Record<string, any> = {};
    const params = element.parameters || {};
    for (const name of TYPE_PARAMETER_NAMES) {
        if (params[name] !== undefined) seed[name] = params[name];
    }
    const geo = element.geometryData;
    if (geo) {
        if (typeof geo.thickness === 'number') seed['thickness'] = geo.thickness;
        if (Array.isArray(geo.layers)) seed['layers'] = geo.layers;
        if (typeof geo.width === 'number') seed['Width'] = geo.width;
        if (typeof geo.height === 'number') seed['Height'] = geo.height;
    }
    familyManager.createType(family.name, ref.typeName, seed);
    return ref;
}

/**
 * Names of parameters that live on the TYPE and must be pushed onto every
 * instance when the type changes. Extends the Match-Type tool classification
 * with the geometry-driving keys this codebase uses on symbols.
 */
export const TYPE_PARAMETER_NAMES: string[] = [
    'Material',
    'Construction Type',
    'Function',
    'Fire Rating',
    'Structural Usage',
    'Structural Role',
    'Structural Material',
    'Frame Material',
    'Panel Material',
    'Glass',
    'Finish',
    'Finish 1 Material',
    'Finish 2 Material',
    'Sill Material',
    'Roof Covering',
    'Duct Material',
    'Pipe Material',
    'Manufacturer',
    'Model',
    'Type Name',
    'Assembly Code',
    'Assembly Description',
    'Keynote',
    'Type Mark',
    'Type Comments',
    'Cost',
    'Url',
    'Description',
    'thickness',
    'Thickness',
    'Width',
    'Height',
    'Depth',
    'Frame Width',
    'Rough Width',
    'Rough Height',
    'Coarse Scale Fill Pattern',
    'Coarse Scale Fill Color',
    'wrappingAtInserts',
    'Wrapping at Inserts',
    'Wrapping at Ends',
    'Enable Analytical Model',
    'Export to IFC As',
    'IFC Predefined Type',
    'Voltage',
    'Apparent Load',
    'Wattage',
    'Initial Intensity',
    'Color Temperature',
    'Text Font',
    'Font Size',
];

/** All elements currently placed with the given type string. */
export function getInstancesOfType(ref: TypeRef): BIMElement[] {
    const typeString = formatTypeString(ref);
    const project = bimKernel.getProject();
    const result: BIMElement[] = [];
    project.elements.forEach(el => {
        if (el.type === typeString) result.push(el);
    });
    return result;
}

/** Count instances per type string, for the dialog's warning line. */
export function countInstancesOfType(ref: TypeRef): number {
    return getInstancesOfType(ref).length;
}

/**
 * Build the per-instance patch that mirrors type parameters onto an element:
 * merge type-classified parameters and sync geometry-driving values into
 * geometryData exactly like DoorTool / WallToolClass derive them on placement.
 */
function buildInstancePatch(element: BIMElement, typeParams: Record<string, any>): Partial<BIMElement> {
    const updates: Partial<BIMElement> = {};
    const oldParams = element.parameters || {};
    const mergedParams: Record<string, any> = { ...oldParams };

    for (const name of TYPE_PARAMETER_NAMES) {
        if (typeParams[name] !== undefined) mergedParams[name] = typeParams[name];
    }
    updates.parameters = mergedParams;

    const geo = element.geometryData;
    if (geo) {
        const mergedGeo: Record<string, any> = { ...geo };
        let changed = false;

        const pickNumber = (...keys: string[]): number | undefined => {
            for (const k of keys) {
                const v = typeParams[k];
                if (typeof v === 'number' && !Number.isNaN(v)) return v;
            }
            return undefined;
        };

        switch (element.category) {
            case 'Walls': {
                const thickness = pickNumber('thickness', 'Thickness');
                if (thickness !== undefined && thickness > 0) { mergedGeo.thickness = thickness; changed = true; }
                const layers = typeParams['layers'] ?? typeParams['Layers'];
                if (Array.isArray(layers) && layers.length > 0) {
                    mergedGeo.layers = layers as CompoundStructureLayer[];
                    mergedGeo.thickness = layers.reduce((sum: number, l: any) => sum + (typeof l.thickness === 'number' ? l.thickness : 0), 0);
                    changed = true;
                }
                break;
            }
            case 'Doors':
            case 'Windows': {
                const width = pickNumber('Width', 'width');
                const height = pickNumber('Height', 'height');
                if (width !== undefined && width > 0) { mergedGeo.width = width; changed = true; }
                if (height !== undefined && height > 0) { mergedGeo.height = height; changed = true; }
                const sill = pickNumber('Sill Height', 'sillHeight');
                if (element.category === 'Windows' && sill !== undefined) { mergedGeo.sillHeight = sill; changed = true; }
                break;
            }
            case 'Floors':
            case 'Roofs':
            case 'Ceilings': {
                const thickness = pickNumber('thickness', 'Thickness');
                if (thickness !== undefined && thickness > 0 && typeof mergedGeo.thickness === 'number') {
                    mergedGeo.thickness = thickness;
                    changed = true;
                }
                break;
            }
            default:
                break;
        }

        if (changed) updates.geometryData = mergedGeo;
    }

    return updates;
}

export interface CommitTypeEditOptions {
    ref: TypeRef;
    /** Full new parameter bag for the symbol (post formula resolution). */
    newParams: Record<string, any>;
    /** Instance element ids whose type string must switch to `ref` (type dropdown changes). */
    switchElementIds?: string[];
}

/**
 * Apply type parameter edits to the symbol AND every instance. Single
 * undoable kernel command. Also used for type switches (switchElementIds
 * non-empty: instances adopt the destination type string + parameters).
 */
export function commitTypeEdit(opts: CommitTypeEditOptions): void {
    const { ref, newParams } = opts;
    const symbol = familyManager.getType(ref.family, ref.typeName);
    if (!symbol) return;

    const oldParams = JSON.parse(JSON.stringify(symbol.parameters));
    const typeString = formatTypeString(ref);

    const project = bimKernel.getProject();
    const batch: Array<{ id: string; updates: Partial<BIMElement> }> = [];

    // 1) Instances already carrying this type receive the parameter diff.
    project.elements.forEach(el => {
        if (el.type !== typeString) return;
        const patch = buildInstancePatch(el, newParams);
        if (Object.keys(patch).length > 0) batch.push({ id: el.id, updates: patch });
    });

    // 2) Explicit switchers adopt the type string plus its parameters.
    (opts.switchElementIds ?? []).forEach(id => {
        const el = project.elements.get(id);
        if (!el || el.type === typeString) return;
        const patch = buildInstancePatch(el, newParams);
        batch.push({ id, updates: { type: typeString, ...patch } });
    });

    bimKernel.applyTypeChange({
        description: `Type Properties: ${ref.typeName}`,
        applyType: () => familyManager.setTypeParameters(ref.family, ref.typeName, newParams),
        revertType: () => familyManager.setTypeParameters(ref.family, ref.typeName, oldParams),
        elementBatch: batch,
    });
}

/** Rename a type and rewrite every instance's type string. Undoable. */
export function renameTypeInProject(ref: TypeRef, newName: string): boolean {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === ref.typeName) return false;
    if (familyManager.getType(ref.family, trimmed)) return false;

    const oldString = formatTypeString(ref);
    const newString = formatTypeString({ family: ref.family, typeName: trimmed });
    const project = bimKernel.getProject();
    const batch: Array<{ id: string; updates: Partial<BIMElement> }> = [];
    project.elements.forEach(el => {
        if (el.type === oldString) batch.push({ id: el.id, updates: { type: newString } });
    });

    bimKernel.applyTypeChange({
        description: `Rename Type: ${trimmed}`,
        applyType: () => { familyManager.renameType(ref.family, ref.typeName, trimmed); },
        revertType: () => { familyManager.renameType(ref.family, trimmed, ref.typeName); },
        elementBatch: batch,
    });
    return true;
}

/**
 * Duplicate a type (deep-copied params) and register the clone in the family.
 * Returns the new type name or null on failure. Registry-only; no instances
 * change, so this is not part of the undo stack (matches BIM, where
 * Duplicate simply creates the new type for the dialog to edit).
 */
export function duplicateTypeInFamily(ref: TypeRef, desiredName?: string): string | null {
    const symbol = familyManager.getType(ref.family, ref.typeName);
    if (!symbol) return null;
    // duplicateType copies the CURRENT parameter state of the source type.
    return familyManager.duplicateType(ref.family, ref.typeName, desiredName);
}

/**
 * Legacy symbol bags occasionally carry lowercase geometry keys (window
 * 'width'/'height', 'sillHeight'). Normalize them onto the schema-canonical
 * names so the Edit Type table renders values in the right rows.
 */
const LEGACY_KEY_ALIASES: Record<string, string> = {
    width: 'Width',
    height: 'Height',
    sillHeight: 'Sill Height',
};

export function normalizeSymbolParameters(parameters: Record<string, any>): Record<string, any> {
    const out: Record<string, any> = { ...parameters };
    for (const [legacy, canonical] of Object.entries(LEGACY_KEY_ALIASES)) {
        if (legacy in out && !(canonical in out)) {
            out[canonical] = out[legacy];
        }
    }
    return out;
}

/** Resolve the live symbol bag for editing (after ensuring registration). */
export function getEditableParameters(element: BIMElement): { ref: TypeRef; parameters: Record<string, any> } | null {
    const ref = ensureSymbolForElement(element);
    const symbol = familyManager.getType(ref.family, ref.typeName);
    if (!symbol) return null;
    return { ref, parameters: normalizeSymbolParameters(JSON.parse(JSON.stringify(symbol.parameters))) };
}
